import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home as HomeIcon, Factory, FileCheck, Wrench, BadgeIndianRupee,
  ArrowUpRight, ClipboardCheck, PenTool, Hammer, PlugZap, Sun,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const services = [
  {
    num: "01", icon: HomeIcon, title: "Residential Rooftop Solar",
    img: "https://images.pexels.com/photos/13558357/pexels-photo-13558357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    text: "Complete home solar from 1 kW to 10 kW. We survey your roof, design around shadows and water tanks, install with leak-proof structures, and hand you a plant that pays for itself in 3–4 years.",
    points: ["Free site survey & shadow analysis", "Leak-proof elevated structures", "App-based generation monitoring"],
  },
  {
    num: "02", icon: Factory, title: "Commercial & Industrial Solar",
    img: "https://images.pexels.com/photos/9875423/pexels-photo-9875423.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    text: "High-yield plants for factories, warehouses, offices and institutions. Engineered for maximum units per square foot, with accelerated depreciation benefits that improve your ROI from year one.",
    points: ["Detailed load & consumption study", "Accelerated depreciation advisory", "SCADA / remote monitoring"],
  },
  {
    num: "03", icon: FileCheck, title: "Net Metering & DISCOM Handling",
    img: "https://images.pexels.com/photos/9875448/pexels-photo-9875448.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    text: "The paperwork nobody wants to do — we do all of it. Applications, inspections, agreements and net meter commissioning with BESCOM and other DISCOMs, end to end.",
    points: ["Complete DISCOM liaison", "Net meter commissioning", "Zero office visits for you"],
  },
  {
    num: "04", icon: Wrench, title: "Maintenance & Panel Cleaning",
    img: "https://images.pexels.com/photos/12243093/pexels-photo-12243093.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    text: "Dust steals up to 25% of generation. Our AMC plans include scheduled cleaning, electrical health checks and annual generation audits so your plant performs like day one.",
    points: ["Scheduled panel cleaning", "Inverter & wiring health checks", "Priority breakdown response"],
  },
  {
    num: "05", icon: BadgeIndianRupee, title: "PM Surya Ghar Subsidy Assistance",
    img: "https://images.pexels.com/photos/13558357/pexels-photo-13558357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    text: "Up to ₹78,000 direct to your bank account. We file your application on the national portal, track approvals, and make sure not one rupee of subsidy is left on the table.",
    points: ["National portal registration & filing", "Document preparation", "Approval tracking till disbursal"],
  },
];

const steps = [
  { icon: ClipboardCheck, title: "Survey", text: "Free rooftop visit, shadow & load analysis." },
  { icon: PenTool, title: "Design", text: "Custom layout, exact savings & quote." },
  { icon: Hammer, title: "Install", text: "1–2 day installation by certified engineers." },
  { icon: PlugZap, title: "Net Meter", text: "Subsidy filed, meter synced, savings begin." },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="relative bg-brand-deep text-white grain overflow-hidden pt-32 pb-20">
        <div className="absolute -top-32 right-0 w-[26rem] h-[26rem] rounded-full bg-gold/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs tracking-[0.35em] text-gold mb-4"
          >
            OUR SERVICES
          </motion.p>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }} animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-3xl"
            >
              From first survey to <span className="text-gold">first unit saved.</span>
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-6 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed"
          >
            Five specialised services, one accountable team. You never chase a vendor,
            a DISCOM office, or a subsidy portal — we carry the whole journey.
          </motion.p>
        </div>
      </section>

      <Marquee dark items={["Free Rooftop Survey", "1–2 Day Installation", "BESCOM Net Metering", "25-Year Support", "Certified Engineers"]} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 space-y-8">
        {services.map((s, i) => (
          <Reveal key={s.num}>
            <article
              data-testid={`service-card-${s.num}`}
              className={`group grid lg:grid-cols-2 gap-0 rounded-[2rem] overflow-hidden border border-brand/10 bg-white transition-shadow duration-500 hover:shadow-[0_24px_60px_rgba(3,59,43,0.14)] ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="relative h-64 lg:h-auto overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/60 to-transparent" />
                <span className="absolute top-5 left-5 rounded-full bg-gold text-brand-deep px-4 py-1.5 font-mono text-xs font-bold tracking-widest">
                  {s.num}
                </span>
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <s.icon size={30} className="text-brand" strokeWidth={1.8} />
                <h2 className="mt-4 font-display font-bold text-2xl md:text-3xl text-ink">{s.title}</h2>
                <p className="mt-4 text-moss leading-relaxed">{s.text}</p>
                <ul className="mt-6 space-y-2.5">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm font-semibold text-ink">
                      <Sun size={15} className="text-gold mt-0.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      <section data-testid="process-section" className="bg-brand-mist/60 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-brand mb-4">HOW IT WORKS</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-ink">
              Four steps. <span className="text-brand">Zero stress.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((st, i) => (
              <Reveal key={st.title} delay={i * 0.1}>
                <div data-testid={`process-step-${i}`} className="relative rounded-3xl bg-white border border-brand/10 p-7 h-full">
                  <span className="absolute top-5 right-6 font-display font-extrabold text-4xl text-gold/50">{i + 1}</span>
                  <div className="w-12 h-12 rounded-2xl bg-brand flex items-center justify-center">
                    <st.icon size={22} className="text-gold" />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-xl text-ink">{st.title}</h3>
                  <p className="mt-2 text-sm text-moss leading-relaxed">{st.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-12 text-center">
            <Link
              to="/contact"
              data-testid="services-cta-survey"
              className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-8 py-4 font-bold hover:scale-105 transition-transform"
            >
              Start with a Free Survey <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
